import qs, { parseUrl } from 'query-string';
import NP from 'number-precision';
import { useTranslation } from 'react-i18next';
import _request, { requestBlob } from './request';
import { AREA_MAP, C_CORP, HK, IP, LIMITED, LLC, LLP, SGP, S_CORP, UK, US } from 'constants/common';
import { COMPANY_GOODS_TYPE } from 'controller/bs';
import { BS } from 'types';
import Notify from 'ui-component/Tools/Notify';

export const mathMultiply = (val1: any, val2: any) => NP.times(val1, val2);

export const transformAmount: any = (amount: number | undefined) => {
  return NP.divide(amount || 0, 100);
};

interface IObjectAny {
  [propName: string]: any;
}

export type Result<T> =
  | {
      success: true;
      data: T;
      response: any; // 原始数据
    }
  | {
      success: false;
      err: Error;
      message: string;
      data?: any;
    };

export const getStartTime = (time: any) => {
  const date = new Date(time);
  return date.setHours(0, 0, 0, 0);
};

export const getEndTime = (time: any) => {
  const date = new Date(time);
  return date.setHours(23, 59, 59, 999);
};

export const getMonthStartTime = (time: any) => {
  const date = new Date(time);
  const year = date.getFullYear();
  const month = date.getMonth();
  return new Date(year, month, 1).getTime();
};
export const getMonthEndTime = (time: any) => {
  const date = new Date(time);
  const year = date.getFullYear();
  const month = date.getMonth();
  return new Date(year, month + 1, 1).getTime() - 1;
};

/**
 * 自动解包多级 data （当 data 存在且仅存在 data 属性）
 * TODO 可以对 request 包一层，避免样板代码
 */
export function normalizeResult<T = any>(res: { err: Error | null; data: any }): Result<T> {
  if (!res) {
    return {
      success: false,
      data: null,
      message: '',
      err: new Error('')
    };
  }
  if (res.err) {
    return {
      success: false,
      err: res.err,
      message: ''
    };
  } else {
    // 第一层 data 是服务端返回的原始 response
    let { data } = res;

    /**
     * data 是数组的情况
     * （还没有发现什么接口什么情况下服务端会直接返回数组，虽然双春说有）
     */
    if (Array.isArray(data)) {
      return {
        success: true,
        data: data as any,
        response: res.data
      };
    }

    /**
     * 返回错误
     */
    if (data.success !== true) {
      return data;
    }

    // 第二层 data 为 response 的 data 属性
    ({ data } = data);

    // 如果 data 下面有且只有一层 data，则进入下一层
    while (data && Object.prototype.hasOwnProperty.call(data, 'data') && Object.keys(data).length === 1) {
      ({ data } = data);
    }

    return {
      success: true,
      data,
      response: res.data
    };
  }
}

// 若传入 format='format' 则走规范
export const dateFormat = (date: string | number | Date, format = 'yyyy-MM-dd hh:mm:ss TT'): string => {
  if (!date) {
    date = new Date();
  }
  if (typeof date === 'string' && /^\d+$/.test(date)) {
    date = new Date(+date);
  }
  if (typeof date === 'number') {
    date = new Date(date);
  }
  if (typeof date !== 'number' && !(date instanceof Date)) {
    date = date.replace(/年|月/g, '-').replace(/日/g, '');
    date = new Date(date);
  }

  const duration = Date.now() - date.getTime();
  const level1 = 60 * 1000; // 1 分钟
  const level2 = 60 * 60 * 1000; // 1 小时
  const level3 = 24 * 60 * 60 * 1000; // 1 天
  const level4 = 2 * 24 * 60 * 60 * 1000; // 2天

  if (format === 'default') {
    if (duration < level1) {
      return '刚刚';
    }
    if (duration >= level1 && duration < level2) {
      return `${Math.round(duration / level1)}分钟前`;
    }
    if (duration >= level2 && duration < level3) {
      return `${Math.round(duration / level2)}小时前`;
    }
    if (duration >= level3 && duration < level4) {
      format = '昨天 hh:mm';
    }
    // 判断是否过年了
    const _date = new Date();
    const _year = _date.getFullYear();
    if (new Date(`${_year}-01-01`).getTime() <= date.getTime()) {
      format = 'MM月dd日 hh:mm';
    } else {
      format = 'yyyy年MM月dd日 hh:mm';
    }
  }

  const o: IObjectAny = {
    'M+': date.getMonth() + 1, // 月份
    'd+': date.getDate(), // 日
    'h+': date.getHours(), // 小时 24进制
    'm+': date.getMinutes(), // 分
    's+': date.getSeconds(), // 秒
    'q+': Math.floor((date.getMonth() + 3) / 3), // 季度
    S: date.getMilliseconds(), // 毫秒
    'T+': date.getHours() < 12 ? 'AM' : 'PM',
    'H+': date.getHours() % 12 === 0 ? 12 : date.getHours() % 12 // 小时 12进制
  };
  if (/(y+)/.test(format)) {
    format = format.replace(RegExp.$1, String(date.getFullYear()).substr(4 - RegExp.$1.length));
  }
  Object.keys(o).forEach((k) => {
    if (new RegExp(`(${k})`).test(format)) {
      format = format.replace(RegExp.$1, RegExp.$1.length === 1 ? o[k] : `00${o[k]}`.substr(String(o[k]).length));
    }
  });
  return format;
};

export const getStringLength = (str: string) => {
  return str.replace(/[\u0391-\uFFE5]/gim, 'aa').length;
};

export const isMobile = () => {
  const userAgentInfo = navigator.userAgent;
  const Agents = ['Android', 'iPhone', 'SymbianOS', 'Windows Phone', 'iPod'];
  let flag = false;

  for (const v of Agents) {
    if (userAgentInfo.includes(v)) {
      flag = true;
      break;
    }
  }
  return flag;
};

// 加载script
export function loadScript(src: string, ignore?: boolean) {
  // 检测是否重复
  if (!ignore) {
    const scripts = document.getElementsByTagName('script');
    let isOwned = false;
    for (let i = 0; i < scripts.length; i++) {
      const _src = scripts[i].getAttribute('src');
      isOwned = isOwned || _src === src;
    }
    if (isOwned) {
      return new Promise((resolve) => {
        resolve('');
      });
    }
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.head && document.head.appendChild(script);
  });
}

// 解析query-string为key-value格式
export const getQuery = (param?: string) => {
  const url = param || window.location.search;
  return parseUrl(url).query;
};

// 休眠 延迟
export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const transformDate = (number: number) => {
  return number >= 10 ? String(number) : `0${number}`;
};

export const getStriptDate = (year?: number, month?: number) => {
  if (!year || !month) return '';
  return `${transformDate(month)}/${String(year).slice(2)}`;
};

export const getTodayTime = () => {
  const date = new Date();
  return `${String(date.getFullYear()).slice(2)}/${transformDate(date.getMonth() + 1)}/${transformDate(date.getDate())}`;
};

// 生成guid
export const guid = () => {
  return (
    (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1) +
    (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1) +
    '-' +
    (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1) +
    '-' +
    (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1) +
    '-' +
    (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1) +
    '-' +
    (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1) +
    (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1) +
    (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1)
  );
};

export const object2string = (options: any) => {
  return qs.stringify(options);
};

// url 接口, data 入参, options 配置
export const request = {
  get: (url: string, data = {}, options?: any) => {
    const query = object2string(data);
    const sep = url.includes('?') ? '&' : '?';
    return _request(`${url}${query ? sep : ''}${query}`);
  },
  getBlob: (url: string, data = {}, options?: any) => {
    const query = object2string(data);
    const sep = url.includes('?') ? '&' : '?';
    return requestBlob(`${url}${query ? sep : ''}${query}`);
  },
  postBlob: (url: string, data = {}, options?: any) => {
    return requestBlob(url, {
      method: 'POST',
      body: options?.stringify ? JSON.stringify(data) : data
    });
  },
  post: (url: string, data = {}, options?: any) => {
    return _request(url, {
      method: 'POST',
      body: options?.stringify ? JSON.stringify(data) : data
    });
  },
  put: (url: string, data = {}, options?: any) => {
    return _request(url, {
      method: 'PUT',
      body: options?.stringify ? JSON.stringify(data) : data
    });
  },
  delete: (url: string, data = {}, options?: any) => {
    const query = object2string(data);
    const sep = url.includes('?') ? '&' : '?';
    return _request(`${url}${query ? sep : ''}${query}`, {
      method: 'DELETE'
    });
  }
};

export const toFixed = (val: string | number = 0, persion = 0) => {
  return Number(val).toFixed(persion);
};

export const isWindows = () => window.navigator.appVersion.includes('Win');

export const initStyle = () => {};

export const flat = (nodes: any[]): any[] => {
  let result: any[] = [];
  if (!nodes || nodes.length === 0) return [];
  nodes.forEach((node) => {
    const newNode = { ...node };
    const { children } = newNode;
    delete newNode.children;
    result.push(newNode);
    const _result = flat(children);
    result = [...result, ..._result];
  });
  return result;
};

export const downloadBlobUrl = (blobUrl: any, name: string) => {
  let a: any = document.createElement('a');
  a.style.display = 'none';
  a.download = name;
  a.href = blobUrl;
  a.click();
  a = null;
};

export const formatFloat = (src: any, pos: any) => {
  // eslint-disable-next-line no-restricted-properties
  return Math.round(src * Math.pow(10, pos)) / Math.pow(10, pos);
};

export const changeTheme = (theme: string) => {
  const root = document.documentElement;
  root.className = theme;
};

export const getTimeState = () => {
  const { t } = useTranslation('common');
  // 获取当前时间
  const timeNow = new Date();
  // 获取当前小时
  const hours = timeNow.getHours();
  // 设置默认文字
  let text = '';
  // 判断当前时间段
  if (hours >= 0 && hours <= 10) {
    text = t('morning');
  } else if (hours > 10 && hours <= 14) {
    text = t('lunch');
  } else if (hours > 14 && hours <= 18) {
    text = t('afternoon');
  } else if (hours > 18 && hours <= 24) {
    text = t('evening');
  }
  return text;
};

export const getLastMonth = (month: number, year: number) => {
  let _year, _lastMonth;
  if (month == 0) {
    //如果是0，则说明是1月份，上一个月就是去年的12月
    _year = year - 1;
    _lastMonth = 11;
  } else {
    year = year;
    _lastMonth = month - 1;
  }

  return {
    year: _year,
    month: _lastMonth
  };
};

export const getNextMonth = (month: number, year: number) => {
  let _year, _lastMonth;
  if (month == 11) {
    //如果是0，则说明是1月份，上一个月就是去年的12月
    _year = year + 1;
    _lastMonth = 0;
  } else {
    year = year;
    _lastMonth = month + 1;
  }

  return {
    year: _year,
    month: _lastMonth
  };
};

//JS 计算两个时间戳相差年月日时分秒
export const calculateDiffTime = (startTime: number, endTime: number) => {
  // @ts-ignore
  let runTime = parseInt(endTime - startTime);
  let year = Math.floor(runTime / 86400 / 365);
  runTime = runTime % (86400 * 365);
  let month = Math.floor(runTime / 86400 / 30);
  runTime = runTime % (86400 * 30);
  let day = Math.floor(runTime / 86400);
  runTime = runTime % 86400;
  let hour = Math.floor(runTime / 3600);
  runTime = runTime % 3600;
  let minute = Math.floor(runTime / 60);
  runTime = runTime % 60;
  let second = runTime;
  let result = {
    year,
    month,
    day,
    hour,
    minute,
    second
  };
  return result;
};

export const RENDER_AREA = (area?: string, language = 'en') =>
  language === 'zh' ? AREA_MAP[area || '']?.text : AREA_MAP[area || '']?.code;


export const renderPrefix = (type: string, t: any) => {
  if (type === 'LLC') return t('LLC', { ns: 'company' });
  if (type === 'CCORP') return t('CCORP', { ns: 'company' });
  return type;
};
export const renderGoodsType = (t: any, typeList: COMPANY_GOODS_TYPE[]) => {
  const text: any = [];
  typeList.forEach((x) => {
    if (x === 'ANNUAL_REPORT') {
      text.push(t('ANNUAL_REPORT', { ns: 'company' }));
    }
    if (x === 'DEREGISTER') {
      text.push(t('DEREGISTER', { ns: 'company' }));
    }
    if (x === 'CHANGE_COMPANY_LOCATION') {
      text.push(t('CHANGE_INFO', { ns: 'company' }));
    }
    if (x === 'CHANGE_COMPANY_NAME') {
      text.push(t('CHANGE_INFO', { ns: 'company' }));
    }
    if (x === 'CHANGE_STOCKHOLDER_NAME') {
      text.push(t('CHANGE_INFO', { ns: 'company' }));
    }
    if (x === 'COMPANY_REGISTER') {
      text.push(t('COMPANY_REGISTER', { ns: 'company' }));
    }
    // if (x === 'EXTRA_SERVICE') {
    //   text.push(t('EXTRA_SERVICE', { ns: 'company' }));
    // }
  });
  return Array.from(new Set(text)).join('、');
};

export const renderOrderProgressStatus = (t: any, type?: string) => {
  if (type === 'INIT') return '未申请';
  if (type === 'APPLYING') return '申请中';
  if (type === 'SUCCESS') return '申请成功';
  if (type === 'UNPAID') return t('status.init', { ns: 'company' });
  if (type === 'INFO_CHECKING') return t('status.INFO_CHECKING', { ns: 'company' });
  if (type === 'INFO_ERROR') return t('status.INFO_ERROR', { ns: 'company' });
  if (type === 'REGISTERING') return t('status.REGISTERING', { ns: 'company' });
  if (type === 'REGISTER_FAILED') return t('status.REGISTER_FAILED', { ns: 'company' });
  if (type === 'REGISTER_SUCCESS') return t('status.REGISTER_SUCCESS', { ns: 'company' });
  return type || '';
};

export const renderWithdrawStatus = (type: string, t: any) => {
  if (type === 'PENDING') return t('status.PENDING', { ns: 'agent' });
  if (type === 'REFUSED') return t('status.REFUSED', { ns: 'agent' });
  if (type === 'SETTLED') return t('status.SETTLED', { ns: 'agent' });
  if (type === 'SETTLEING') return t('status.SETTLEING', { ns: 'agent' });
  return type || '';
};

export const renderCompanyType = (t: any, type?: string) => {
  if (type === C_CORP) {
    return 'Corporation (C-Corp)';
  }
  if (type === S_CORP) {
    return 'S-Corp';
  }
  if (type === LLC) {
    return 'LLC';
  }
  if (type === LIMITED) {
    return 'Limited';
  }
  if (type === IP) {
    return 'Individual Proprietorship';
  }
  if (type === LLP) {
    return 'Limited Liability Partnership';
  }
  return type;
};

export const renderCapital = (country: BS.COUNTRY, val?: string) => {
  if (country === US) {
    return `$${val ? val : 1000}`;
  }

  if (country === HK) {
    return `${val ? val : 10000}`;
  }

  if (country === UK) {
    return `£${val ? val : 1000}`;
  }

  if (country === SGP) {
    return `${val ? val : 10000}`;
  }
  return val;
};

export const getStringChar = (val: string) => {
  if (!val) return '';
  if (val.length === 1) return val;
  return val.slice(0, 2);
};

function fallbackCopy(text: string, t?: any) {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    document.execCommand('copy');
    Notify('success', t ? t('tips.copySuccess', { ns: 'common' }) : 'Copied');
  } catch (err) {
    console.log ('复制失败');
  }
  document.body.removeChild(textarea);
}

export const copyToClip = async (text: string, t?: any) => {
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    await navigator.clipboard.writeText(text);
    Notify('success', t ? t('tips.copySuccess', { ns: 'common' }) : 'Copied');
  } else {
    fallbackCopy(text, t);
  }
}